import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ApiKeysService, ApiKey, ProviderId, ApiProvider, ServiceKind,
} from '../../services/api-keys.service';

export type ApiKeyPreview = Pick<ApiKey, 'provider' | 'model' | 'note' | 'secretMasked'>;

/**
 * Add or edit one external-service key: pick the provider, paste the key, and for
 * chat providers give the model — then it's stored. Writes through the api_keys API.
 */
@Component({
  selector: 'app-api-key-editor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './api-key-editor.component.html',
  styleUrls: ['../configuration-form.css', './api-key-editor.component.css'],
})
export class ApiKeyEditorComponent implements OnInit, OnDestroy {
  @Input() service?: ServiceKind;
  @Input() credential?: ApiKey;
  @Input() inline = false;
  /** Explicit UI preview: supplied options bypass the catalog and never write to the vault. */
  @Input() previewProviders?: ApiProvider[];
  @Input() credentialOnly = false;
  @Input() defaultModel?: string;
  @Output() previewSaved = new EventEmitter<ApiKeyPreview>();
  @Output() saved = new EventEmitter<ApiKey | undefined>();
  @Output() cancelled = new EventEmitter<void>();
  form!: FormGroup;
  get editing(): ApiKey | undefined { return this.credential; }
  private readonly destroyed$ = new Subject<void>();
  saving = false;
  error: string | null = null;
  /** Provider options offered: narrowed to the panel's service when adding. */
  availableProviders: ApiProvider[] = [];

  constructor(private readonly fb: FormBuilder, public readonly store: ApiKeysService) {}

  ngOnInit(): void {
    const service = this.editing?.service ?? this.service;
    this.availableProviders = this.previewProviders ?? (service
      ? this.store.providers.filter(p => p.service === service)
      : this.store.providers);
    const presetProvider: ProviderId =
      this.editing?.provider ?? this.availableProviders[0]?.id ?? '';

    this.form = this.fb.group({
      // Provider is fixed while editing — switching provider = remove + re-add.
      provider: [{ value: presetProvider, disabled: !!this.editing }, Validators.required],
      // On edit, a blank key means "keep the current one".
      secret:   ['', this.editing
        ? [Validators.maxLength(4096)]
        : [Validators.required, Validators.maxLength(4096)]],
      model:    [this.editing?.model ?? this.defaultModel ?? ''],
      note:     [this.editing?.note ?? '', this.credentialOnly ? [Validators.required, Validators.maxLength(80), Validators.pattern(/\S/)] : Validators.maxLength(80)],
    });

    this.applyModelValidator(this.currentMeta?.needsModel ?? false);
    this.form.get('provider')!.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((id: ProviderId) =>
      this.applyModelValidator(this.availableProviders.find(p => p.id === id)?.needsModel ?? false));
  }

  get currentMeta(): ApiProvider | undefined { return this.availableProviders.find(p => p.id === this.form.getRawValue().provider); }

  private applyModelValidator(needs: boolean): void {
    const m = this.form.get('model')!;
    m.setValidators(needs ? [Validators.required, Validators.maxLength(128)] : []);
    m.updateValueAndValidity({ emitEvent: false });
  }

  save(): void {
    if (this.form.invalid || this.saving || !this.currentMeta) return;
    const v = this.form.getRawValue();
    const model = this.currentMeta.needsModel ? (v.model?.trim() || null) : null;
    const note = v.note?.trim() || null;
    if (this.previewProviders !== undefined) {
      this.form.get('secret')!.reset('');
      this.previewSaved.emit({ provider: v.provider, model, note, secretMasked: '••••••••' });
      return;
    }
    this.saving = true;
    this.error = null;

    const done = (key?: ApiKey) => { this.form.get('secret')!.reset(''); this.saving = false; this.saved.emit(key); };
    const fail = () => { this.saving = false; this.error = 'Could not save the key.'; };

    if (this.editing) {
      this.store.update(this.editing.id, { secret: v.secret || null, model, note })
        .pipe(takeUntil(this.destroyed$)).subscribe({ next: () => done(), error: fail });
    } else {
      this.store.add({ service: this.currentMeta.service, provider: v.provider, secret: v.secret, model, note })
        .pipe(takeUntil(this.destroyed$)).subscribe({ next: done, error: fail });
    }
  }

  cancel(): void { this.form.get('secret')!.reset(''); this.cancelled.emit(); }
  ngOnDestroy(): void { this.form.get('secret')?.reset(''); this.destroyed$.next(); this.destroyed$.complete(); }
}
